import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import { dropTask } from 'ember-concurrency';
import set from 'ember-set-helper/helpers/set';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { eq, isEmpty, or } from 'ember-truth-helpers';
import ButtonGroup from 'garaje/components/ui/button-group';
import type Group from 'garaje/models/group';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MetricsService from 'garaje/services/metrics';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';

interface GroupsTableRowSignature {
  Args: {
    /**
     * List of groups to check duplicated names
     */
    groups: Group[];
    /**
     * Group to edit
     */
    group: Group;
    /**
     * Action to show the confirmation modal
     */
    confirmGroupDeletion: (group: Group) => void;
  };
  Element: HTMLTableRowElement;
}

/**
 * Group row to edit a group
 */
export default class GroupsTableRow extends Component<GroupsTableRowSignature> {
  @tracked declare element: HTMLTableRowElement;

  @service declare flashMessages: FlashMessagesService;
  @service declare metrics: MetricsService;

  @tracked isEditing = false;
  @tracked updatedName = '';

  // eslint-disable-next-line ember/classic-decorator-hooks
  constructor(owner: unknown, args: GroupsTableRowSignature['Args']) {
    super(owner, args);
    this.updatedName = this.args.group.name;
  }

  saveGroupTask = dropTask(async () => {
    const duplicatedName = this.args.groups.some((group) => group.name === this.updatedName.trim());
    if (duplicatedName) {
      this.flashMessages.showAndHideFlash('error', 'Group name taken', 'Choose a group name not already in use');
      return;
    }
    try {
      const originalGroupName = this.args.group.name;
      this.args.group.name = this.updatedName.trim();
      await this.args.group.save();

      this.metrics.trackEvent('Group Action Taken', {
        action: 'group_name_edited',
        original_group_name: originalGroupName,
        new_group_name: this.args.group.name,
      });

      this.isEditing = false;
    } catch (e) {
      const errorText = parseErrorForDisplay(e);
      this.flashMessages.showAndHideFlash('error', errorText);
    }
  });

  @action
  edit(): void {
    this.isEditing = true;
    // eslint-disable-next-line ember/no-runloop
    next(this, function () {
      this.element.getElementsByTagName('input')[0]?.focus();
    });
  }

  @action
  registerElement(element: HTMLTableRowElement): void {
    this.element = element;
  }

  <template>
    <tr data-test-group-row={{@group.id}} {{didInsert this.registerElement}}>
      <td class="p-0 w-1/2">
        <div class="pl-4 py-4 border-t">
          {{#if this.isEditing}}
            <input
              data-test-text-editor-input
              class="unset-global-margin"
              type="text"
              value={{this.updatedName}}
              {{on "input" (pick "target.value" (set this "updatedName"))}}
            />
          {{else}}
            <div class="mt-2 mb-1">
              {{@group.name}}
            </div>
          {{/if}}
        </div>
      </td>
      <td data-test-group-total-locations class="p-0 w-1/4">
        <div class="pl-4 pb-4 pt-6 border-t">
          {{! @glint-expect-error }}
          {{@group.locations.length}}
        </div>
      </td>
      <td class="p-0 border-t align-middle" style="width: 170px;">
        {{#if this.isEditing}}
          <ButtonGroup class="flex justify-end">
            <button
              data-test-cancel-edition
              class="btn btn--outline btn--small"
              disabled={{this.saveGroupTask.isRunning}}
              {{on "click" (fn (mut this.isEditing) false)}}
              tabindex="-1"
            >
              Cancel
            </button>
            <button
              data-test-save-group
              class="btn btn--brand btn--small"
              disabled={{or
                this.saveGroupTask.isRunning
                (or (isEmpty this.updatedName) (eq this.updatedName @group.name))
              }}
              {{on "click" this.saveGroupTask.perform}}
              tabindex="-1"
            >
              {{if this.saveGroupTask.isRunning "Saving..." "Save"}}
            </button>
          </ButtonGroup>
        {{else}}
          <div class="flex justify-end">
            <button data-test-edit-group class="px-4 pt-1" {{on "click" this.edit}} tabindex="-1">
              {{svgJar "pencil-dark-full" height="16" width="16"}}
            </button>
            <button
              data-test-delete-group
              class="px-4 pt-1"
              {{on "click" (fn @confirmGroupDeletion @group)}}
              tabindex="-1"
            >
              {{svgJar "trash-delete-black" height="16" width="16"}}
            </button>
          </div>
        {{/if}}
      </td>
    </tr>
  </template>
}
